import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { CATEGORIES, HERO_IMAGES, localize } from '../data/mock';
import { fetchProperties } from '../data/api';
import PropertyCard from '../components/PropertyCard';
import Reveal from '../components/Reveal';

const Home = () => {
  const { t, lang } = useLang();
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    let active = true;
    fetchProperties().then((data) => {
      if (!active) return;
      const pinned = data.filter((p) => p.featured);
      if (pinned.length > 0) {
        setFeatured(pinned.slice(0, 6));
        return;
      }
      // fallback: one per category, then fill to 6
      const byCat = {};
      const order = ['hotels', 'immeubles', 'exception', 'liquidation'];
      data.forEach((p) => {
        if (!byCat[p.category]) byCat[p.category] = [];
        byCat[p.category].push(p);
      });
      const picks = [];
      order.forEach((c) => { if (byCat[c]?.[0]) picks.push(byCat[c][0]); });
      order.forEach((c) => { if (byCat[c]?.[1]) picks.push(byCat[c][1]); });
      setFeatured(picks.slice(0, 6));
    });
    return () => { active = false; };
  }, []);

  const values = [
    { t: t('values.v1t'), d: t('values.v1d') },
    { t: t('values.v2t'), d: t('values.v2d') },
    { t: t('values.v3t'), d: t('values.v3d') },
    { t: t('values.v4t'), d: t('values.v4d') },
  ];

  return (
    <div>
      {/* ===================== HERO ===================== */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGES.interior}
            alt="STARKKE Immobilier"
            className="w-full h-full object-cover kenburns"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#f4efe6]/95 via-[#f4efe6]/55 to-[#1b2331]/45" />
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 w-full pt-28">
          <div className="max-w-3xl">
            <p className="font-sans-elegant text-[0.7rem] md:text-xs tracking-brand text-[#b0894f] mb-8 animate-[fadeUp_0.9s_ease-out]">
              {t('hero.eyebrow')}
            </p>
            <h1 className="brand-mark text-[#1b2331] text-5xl md:text-7xl lg:text-8xl leading-none">
              STARKKE
            </h1>
            <p className="font-sans-elegant text-[#b0894f] tracking-brand text-sm md:text-base mt-3 mb-8">
              IMMOBILIER
            </p>
            <div className="hairline w-16 mb-8" />
            <h2 className="font-serif-display text-3xl md:text-5xl text-[#1b2331] font-light leading-tight mb-6">
              {t('hero.title')}
            </h2>
            <p className="font-sans-elegant text-[0.7rem] md:text-xs tracking-wide-2 text-[#5a5d63] uppercase mb-10">
              {t('hero.subtitle')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/biens"
                className="group inline-flex items-center justify-center gap-3 bg-[#1b2331] text-[#f4efe6] font-sans-elegant text-xs tracking-wide-2 uppercase px-8 py-4 hover:bg-[#b0894f] transition-colors duration-400"
              >
                {t('hero.cta')}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-3 border border-[#1b2331]/40 text-[#1b2331] font-sans-elegant text-xs tracking-wide-2 uppercase px-8 py-4 hover:border-[#1b2331] hover:bg-[#1b2331] hover:text-[#f4efe6] transition-all duration-400"
              >
                {t('hero.cta2')}
              </Link>
            </div>
          </div>
        </div>

        {/* right side vertical caption */}
        <div className="hidden xl:block absolute right-10 top-1/2 -translate-y-1/2 text-right">
          <div className="hairline w-px h-24 bg-[#b0894f] ml-auto mb-6" />
          <p className="font-serif-display italic text-xl text-[#f4efe6] leading-snug max-w-[220px]">
            {t('hero.quote')}
          </p>
          <p className="font-sans-elegant text-[0.62rem] tracking-brand text-[#f4efe6]/80 mt-8">
            {t('hero.regions')}
          </p>
        </div>
      </section>

      {/* ===================== INTRO ===================== */}
      <section className="py-24 md:py-32 bg-[#f4efe6]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
            <div className="lg:col-span-7">
              <Reveal>
                <p className="font-sans-elegant text-[0.7rem] tracking-brand text-[#b0894f] mb-6">
                  {t('intro.eyebrow')}
                </p>
                <h2 className="font-serif-display text-3xl md:text-5xl text-[#1b2331] font-light leading-tight mb-8">
                  {t('intro.title')}
                </h2>
                <p className="font-sans-elegant text-[#5a5d63] leading-relaxed text-lg max-w-2xl">
                  {t('intro.text')}
                </p>

                <div className="grid grid-cols-3 gap-x-20 gap-y-6 mt-14 max-w-2xl">
                  {[
                    { n: '+100', l: t('intro.stat1') },
                    { n: '3', l: t('intro.stat2') },
                    { n: '20+', l: t('intro.stat3') },
                  ].map((s, i) => (
                    <div key={i}>
                      <p className="font-serif-display text-4xl text-[#1b2331] whitespace-nowrap">{s.n}</p>
                      <div className="hairline w-8 my-3" />
                      <p className="font-sans-elegant text-[0.68rem] tracking-wide-2 uppercase text-[#8a8d93] leading-relaxed">
                        {s.l}
                      </p>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-5">
              <Reveal delay={150}>
                <div className="img-zoom aspect-[3/4]">
                  <img
                    src={HERO_IMAGES.interior2}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== SECTIONS / CATEGORIES ===================== */}
      <section className="py-24 md:py-32 bg-[#1b2331]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-20">
              <p className="font-sans-elegant text-[0.7rem] tracking-brand text-[#c8a469] mb-6">
                {t('sections.eyebrow')}
              </p>
              <h2 className="font-serif-display text-3xl md:text-5xl text-[#f4efe6] font-light leading-tight">
                {t('sections.title')}
              </h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {CATEGORIES.map((c, i) => (
              <Reveal key={c.slug} delay={i * 100}>
                <Link to={`/biens?cat=${c.slug}`} className="group block relative img-zoom aspect-[16/10] overflow-hidden">
                  <img src={c.cover} alt={localize(c.label, lang)} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1b2331] via-[#1b2331]/30 to-transparent" />
                  <div className="absolute inset-0 p-8 lg:p-10 flex flex-col justify-end">
                    <p className="font-sans-elegant text-[0.62rem] tracking-brand text-[#c8a469] mb-3">
                      {localize(c.tagline, lang)}
                    </p>
                    <div className="flex items-end justify-between">
                      <h3 className="font-serif-display text-3xl lg:text-4xl text-[#f4efe6] leading-tight">
                        {localize(c.label, lang)}
                      </h3>
                      <span className="flex items-center gap-2 font-sans-elegant text-[0.68rem] tracking-wide-2 uppercase text-[#f4efe6] opacity-70 group-hover:opacity-100 group-hover:text-[#c8a469] transition-all">
                        {t('sections.explore')}
                        <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                      </span>
                    </div>
                    <p className="font-sans-elegant text-sm text-[#f4efe6]/60 mt-4 max-w-md leading-relaxed max-h-0 opacity-0 group-hover:max-h-24 group-hover:opacity-100 transition-all duration-500 overflow-hidden">
                      {localize(c.desc, lang)}
                    </p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== FEATURED ===================== */}
      <section className="py-24 md:py-32 bg-[#f4efe6]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
              <div>
                <p className="font-sans-elegant text-[0.7rem] tracking-brand text-[#b0894f] mb-6">
                  {t('featured.eyebrow')}
                </p>
                <h2 className="font-serif-display text-3xl md:text-5xl text-[#1b2331] font-light leading-tight">
                  {t('featured.title')}
                </h2>
              </div>
              <Link
                to="/biens"
                className="group inline-flex items-center gap-2 font-sans-elegant text-xs tracking-wide-2 uppercase text-[#1b2331] link-underline"
              >
                {t('featured.viewAll')}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
            {featured.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 100}>
                <PropertyCard property={p} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== VALUES ===================== */}
      <section className="py-24 md:py-32 bg-[#eae2d3]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="max-w-2xl mb-16">
              <p className="font-sans-elegant text-[0.7rem] tracking-brand text-[#b0894f] mb-6">
                {t('values.eyebrow')}
              </p>
              <h2 className="font-serif-display text-3xl md:text-5xl text-[#1b2331] font-light leading-tight">
                {t('values.title')}
              </h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
            {values.map((v, i) => (
              <Reveal key={i} delay={i * 100}>
                <div className="border-t border-[#1b2331]/20 pt-6">
                  <span className="font-serif-display text-2xl text-[#b0894f]">0{i + 1}</span>
                  <h3 className="font-serif-display text-2xl text-[#1b2331] mt-4 mb-3 leading-tight">{v.t}</h3>
                  <p className="font-sans-elegant text-sm text-[#5a5d63] leading-relaxed">{v.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== CTA ===================== */}
      <section className="relative py-28 md:py-36 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMAGES.geneva} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1b2331]/80" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
          <Reveal>
            <div className="hairline w-16 mx-auto mb-10" />
            <h2 className="font-serif-display text-3xl md:text-5xl text-[#f4efe6] font-light leading-tight max-w-3xl mx-auto mb-6">
              {t('cta.title')}
            </h2>
            <p className="font-sans-elegant text-[#f4efe6]/70 max-w-xl mx-auto mb-10 leading-relaxed">
              {t('cta.text')}
            </p>
            <Link
              to="/contact"
              className="group inline-flex items-center gap-3 bg-[#b0894f] text-[#f4efe6] font-sans-elegant text-xs tracking-wide-2 uppercase px-10 py-4 hover:bg-[#f4efe6] hover:text-[#1b2331] transition-colors duration-400"
            >
              {t('cta.button')}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
